/**
 * 特定の delivery のシリアル番号を編集するダイアログ。
 * 顧客詳細「所有商品」タブと商談詳細「請求・発注・発送」タブの双方から呼び出す。
 *
 * 使い方:
 *  <SerialNumbersEditDialog
 *    open dealId="..." deliveryId="..."
 *    initialLineItems={delivery.lineItems}
 *    onOpenChange={...}
 *  />
 *
 * 「下書き保存」と「納品完了にする」の2モードを提供する。
 */
import type { DeliveryLineItem } from '@goenhub/shared';
import { countDeliverySerials, padSerialNumbers, serialFieldLabel } from '@goenhub/shared';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useUpdateDelivery } from '@/hooks/useSalesOps';

interface Props {
  open: boolean;
  dealId: string;
  deliveryId: string;
  deliveryNumber: string;
  initialLineItems: DeliveryLineItem[];
  onOpenChange: (open: boolean) => void;
}

/** 台数分のシリアル欄を用意した明細のコピー (空配列の発送でも入力欄を出すため)。 */
function withSerialSlots(lineItems: DeliveryLineItem[]): DeliveryLineItem[] {
  return lineItems.map((it) => ({ ...it, serialNumbers: padSerialNumbers(it) }));
}

export function SerialNumbersEditDialog({
  open,
  dealId,
  deliveryId,
  deliveryNumber,
  initialLineItems,
  onOpenChange,
}: Props) {
  const update = useUpdateDelivery();
  const [items, setItems] = useState<DeliveryLineItem[]>(() => withSerialSlots(initialLineItems));

  // 開かれたときに初期化
  useEffect(() => {
    if (open) setItems(withSerialSlots(initialLineItems));
  }, [open, initialLineItems]);

  const handleChange = (itemIdx: number, serialIdx: number, value: string) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== itemIdx) return it;
        const next = [...it.serialNumbers];
        next[serialIdx] = value;
        return { ...it, serialNumbers: next };
      }),
    );
  };

  const allFilled = countDeliverySerials(items).missing === 0;

  const handleSave = async (markCompleted: boolean) => {
    try {
      await update.mutateAsync({
        dealId,
        deliveryId,
        lineItems: items,
        ...(markCompleted ? { status: 'completed' as const } : {}),
      });
      toast.success(
        markCompleted
          ? '納品完了に更新しました。シリアルから稼働機器を自動登録します。'
          : 'シリアルを保存しました',
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>シリアル番号を入力</DialogTitle>
          <DialogDescription>
            発送 {deliveryNumber} の各機器にシリアル番号を入力してください。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[480px] overflow-y-auto">
          {items.map((item, itemIdx) => (
            <div
              key={itemIdx}
              className="rounded-md border border-[var(--border)] p-3 bg-[var(--bg-surface)] space-y-2"
            >
              <div className="text-sm font-medium">
                {item.name}
                <span className="text-xs text-[var(--text-muted)] ml-2">{item.modelName}</span>
              </div>
              {item.deviceKind === 'none' ? (
                // 入荷・検品で「シリアルなし」(ソフト等) にした行は入力欄を出さない
                <p className="text-xs text-[var(--text-muted)]">シリアルなし（機器ではない商品）</p>
              ) : (
                <div className="grid gap-1.5">
                  {item.serialNumbers.map((s, sIdx) => (
                    <div key={sIdx} className="flex items-center gap-2">
                      <span className="text-xs text-[var(--text-muted)] w-6">#{sIdx + 1}</span>
                      <Input
                        value={s}
                        onChange={(e) => handleChange(itemIdx, sIdx, e.target.value)}
                        placeholder={serialFieldLabel(item.deviceKind)}
                        className="font-mono text-xs"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            キャンセル
          </Button>
          <Button variant="secondary" onClick={() => handleSave(false)} disabled={update.isPending}>
            下書き保存
          </Button>
          <Button
            variant="primary"
            onClick={() => handleSave(true)}
            disabled={!allFilled || update.isPending}
          >
            {update.isPending ? '更新中…' : '納品完了 (機器を登録)'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
